import { useState } from "react";
import { PAY_METHOD_BY_KEY, PAY_METHODS, SECURITIES, SECURITY_BY_KEY, defaultMethod, defaultSecurities, methodLabel, nis, securityList } from "@rental/shared";
import { C, inputStyle } from "./theme.js";

/* ============================================================================
   בחירת ביטחונות לשכירות — בחירה מרובה.

   הביטחונות נסגרים מול כל דייר בנפרד: אחד נותן ערבות בנקאית, אחר שיק
   פתוח, שלישי שלושה חודשי פיקדון. לכן זו רשימה לכל שכירות ולא הגדרה
   גלובלית אחת.

   value === undefined פירושו "לא הוגדר לשכירות הזו" ולכן משתמשים בברירת
   המחדל מההגדרות. מערך ריק פירושו "בלי ביטחונות" — בחירה מפורשת. שני
   המצבים נראים אותו דבר על המסך אם לא מבחינים ביניהם, ולכן יש כאן מתג.
   ============================================================================ */

/* תיאור הבחירה בשורה אחת — לסיכום האשף וללוג השיחה. */
export function securitiesLabel(value, rent = 0, settings = {}) {
  if (!Array.isArray(value)) return "ביטחונות לפי ברירת המחדל";
  const list = securityList({ rent, securities: value }, settings);
  if (!list.length) return "בלי ביטחונות";
  return list
    .map((v) => (v.months
      ? `${SECURITY_BY_KEY[v.k].label} — ${v.months} חודשים (${nis(v.amount)})`
      : `${SECURITY_BY_KEY[v.k].label} — ${nis(v.amount)}`))
    .join(" · ");
}

/* אותו בורר, עטוף לשימוש בתוך שיחת העוזר. האשף עובד בטקסט חופשי, אבל
   בחירה מרובה עם סכומים היא לא משהו שכדאי לנחש מתוך משפט — עדיף לפתוח
   את אותו רכיב שמופיע בטופס ולהחזיר ממנו ערך מובנה. */
export function SecuritiesPick({ rent = 0, settings = {}, initial = null, onPick, months = 12, initialRentPay = null }) {
  /* initial / initialRentPay — הבחירה הקודמת, כשהאשף חוזר לשאלה הזו כדי לתקן אותה. */
  const [v, setV] = useState(() => (Array.isArray(initial) ? initial : defaultSecurities(settings)));
  /* תשלום שכר הדירה מוצג באותו מסך. בלי בחירה מפורשת לא נקבע כלום —
     "12 שיקים" כברירת מחדל הכניס סעיף שיקים לחוזה של מי שכלל לא משלם
     כך (24.8.2026). ריק = ברירת המחדל מההגדרות. */
  const [rp, setRp] = useState(() => (initialRentPay && initialRentPay.method ? initialRentPay : { method: "", checks: 0 }));
  return (
    <div className="mt-2">
      <SecuritiesPicker value={v} rent={rent} settings={settings}
        onChange={(next) => setV(Array.isArray(next) ? next : defaultSecurities(settings))}
        rentPay={rp} onRentPay={setRp} months={months} />
      <button type="button" onClick={() => onPick(v, rp)}
        className="mt-2 w-full rounded-lg px-3 py-2 text-[12px]"
        style={{ background: C.teal, color: "#fff" }}>
        אישור הביטחונות והתשלום
      </button>
    </div>
  );
}

/* תיאור תשלום שכר הדירה בשורה — לסיכום האשף וללוג. */
export function rentPayText(rp) {
  if (!rp || !rp.method) return "";
  if (rp.method === "check") return `${rp.checks || 12} שיקים חודשיים`;
  return PAY_METHODS.find((m) => m.k === rp.method)?.label || rp.method;
}

/**
 * rentPay / onRentPay (אופציונלי): כשמועברים, מתחת לביטחונות מופיע גם
 * "תשלום שכר הדירה" — שיקים חודשיים או אמצעי אחר, בלי ברירת מחדל: מה
 * שלא נבחר במפורש נשאר על ברירת המחדל מההגדרות. months = חודשי החוזה,
 * מספר השיקים המוצע כשבוחרים שיקים.
 */
export default function SecuritiesPicker({ value, onChange, rent = 0, settings = {}, rentPay = null, onRentPay = null, months = 12 }) {
  const usingDefaults = !Array.isArray(value);
  const list = usingDefaults ? defaultSecurities(settings) : value;

  const byKey = Object.fromEntries(list.map((x) => [x.k, x]));

  const commit = (next) => onChange(next);

  const toggle = (spec) => {
    if (byKey[spec.k]) {
      commit(list.filter((x) => x.k !== spec.k));
    } else {
      commit([
        ...list,
        spec.unit === "months" ? { k: spec.k, months: 2 } : { k: spec.k, amount: 0 },
      ]);
    }
  };

  const patch = (k, p) =>
    commit(list.map((x) => (x.k === k ? { ...x, ...p } : x)));

  const total = list.reduce((t, sel) => {
    const spec = SECURITIES.find((x) => x.k === sel.k);
    if (!spec) return t;
    return t + (spec.unit === "months"
      ? Math.round(rent * (Number(sel.months) || 0))
      : Math.round(Number(sel.amount) || 0));
  }, 0);

  return (
    <div className="rounded-lg border p-3" style={{ borderColor: C.line }}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-sm font-medium">ביטחונות</div>
          <div className="text-[11px]" style={{ color: C.muted }}>
            {usingDefaults
              ? "לפי ברירת המחדל בהגדרות. בחירה כאן תקבע לשכירות הזו בלבד."
              : "נקבע לשכירות הזו."}
          </div>
        </div>
        {!usingDefaults && (
          <button type="button" onClick={() => commit(undefined)}
            className="text-[11px] shrink-0" style={{ color: C.muted }}>
            חזרה לברירת המחדל
          </button>
        )}
      </div>

      <div className="grid gap-1.5">
        {SECURITIES.map((spec) => {
          const sel = byKey[spec.k];
          const on = !!sel;

          return (
            <div key={spec.k} className="rounded-lg"
              style={{ background: on ? "#F1F4F1" : "transparent" }}>
              <label className="flex items-center gap-2 text-sm px-2 py-1.5 cursor-pointer">
                <input type="checkbox" checked={on} onChange={() => toggle(spec)} />
                <span>{spec.label}</span>
                {on && spec.unit === "months" && Number(sel.months) > 0 && rent > 0 && (
                  <span className="text-[11px] mr-auto" style={{ color: C.muted }}>
                    {nis(Math.round(rent * Number(sel.months)))}
                  </span>
                )}
              </label>

              {/* אפס לא מוצג: ערך 0 בשדה מספר נשאר לפני מה שמקלידים אחריו
                  ("010000"), כי React לא מתקן "01" ל-"1" — שניהם אותו מספר.
                  שדה ריק עם placeholder לא יכול ליפול לזה. */}
              {on && (
                <div className="px-2 pb-2 flex gap-2">
                  {spec.unit === "months" ? (
                    <input type="number" min="0" max="12" inputMode="numeric"
                      aria-label="חודשים" style={{ ...inputStyle, maxWidth: 110 }}
                      value={sel.months || ""} placeholder="חודשים"
                      onChange={(e) => patch(spec.k, { months: Number(e.target.value) || 0 })} />
                  ) : (
                    <input type="number" min="0" inputMode="numeric"
                      aria-label="סכום בשקלים" style={{ ...inputStyle, maxWidth: 130 }}
                      value={sel.amount || ""} placeholder="₪"
                      onChange={(e) => patch(spec.k, { amount: Number(e.target.value) || 0 })} />
                  )}

                  {spec.expiry && (
                    <input type="date" aria-label="בתוקף עד" style={inputStyle}
                      value={sel.expiry || ""}
                      onChange={(e) => patch(spec.k, { expiry: e.target.value })} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {list.length === 0 && (
        <p className="text-[11px] mt-2" style={{ color: C.ochre }}>
          לא נבחר אף ביטחון. בחוזה ייכתב במפורש שלא נמסרו ביטחונות.
        </p>
      )}

      {total > 0 && (
        <div className="text-[12px] mt-2 pt-2" style={{ color: C.muted, borderTop: `1px solid ${C.line}` }}>
          סך הביטחונות: <strong style={{ color: C.ink }}>{nis(total)}</strong>
        </div>
      )}

      {onRentPay && (() => {
        const rp = rentPay && rentPay.method ? rentPay : { method: "", checks: 0 };
        const byChecks = rp.method === "check";
        const defRentMethod = defaultMethod(settings, "rent");
        return (
          <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${C.line}` }}>
            <div className="text-sm font-medium">תשלום שכר הדירה</div>
            {/* שם ברירת המחדל גם בטקסט הרגיל וגם בתוך ה-select עצמו: תיבת
                הבחירה צרה (220px), ולכן בתוכה מוצגת התווית הקצרה
                (PAY_METHOD_BY_KEY[...].short, למשל "ה״ק" ולא "הוראת קבע")
                כדי שלא תיחתך — אבל היא כן מופיעה שם, לא רק כאן למעלה. */}
            <div className="text-[11px] mb-1.5" style={{ color: C.muted }}>
              נכנס לסעיף דמי השכירות בחוזה. בלי בחירה — לפי ברירת המחדל בהגדרות, כרגע{" "}
              <b>{methodLabel(defRentMethod)}</b>.
            </div>
            <select aria-label="אמצעי תשלום שכר הדירה" style={{ ...inputStyle, maxWidth: 220 }} value={rp.method}
              onChange={(e) => onRentPay(e.target.value === "check"
                ? { method: "check", checks: months }
                : { method: e.target.value, checks: 0 })}>
              <option value="">ברירת המחדל ({PAY_METHOD_BY_KEY[defRentMethod]?.short || methodLabel(defRentMethod)})</option>
              {PAY_METHODS.map((m) => <option key={m.k} value={m.k}>{m.label}</option>)}
            </select>
            {byChecks && (
              <div className="flex items-center gap-2 mt-1.5">
                <input type="number" min="1" max="60" inputMode="numeric" aria-label="מספר שיקים"
                  style={{ ...inputStyle, maxWidth: 110 }} value={rp.checks || ""} placeholder={String(months)}
                  onChange={(e) => onRentPay({ method: "check", checks: Number(e.target.value) || 0 })} />
                <span className="text-[12px]" style={{ color: C.muted }}>שיקים</span>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
